import { storage } from "./storage/storage";
import { api, apiWithETag } from "./api";

type StorageArea = "session" | "local";

export interface ApiCacheOptions<T = any> {
	key: string;
	endpoint: string;
	ttlMs?: number;
	area?: StorageArea;
	validate?: (data: T) => boolean;
	skipCache?: boolean; // Se true, ignora cache e sempre busca da API
}

export interface ApiCacheETagOptions<T = any> {
	key: string;
	endpoint: string;
	ttlMs?: number;
	area?: StorageArea;
	validate?: (data: T) => boolean;
	/** Se true, força busca ignorando cache (mas ainda envia ETag para 304) */
	forceRefresh?: boolean;
}

export interface ApiCacheETagResult<T = any> {
	data: T;
	/** Indica se os dados vieram do cache local (304 Not Modified) */
	fromCache: boolean;
	/** ETag atual dos dados */
	etag?: string;
}

/**
 * Busca dados da API com cache simples baseado em TTL.
 * (Mantido para compatibilidade - usar getWithCacheETag para novas implementações)
 */
export async function getWithCache<T = any>({
	key,
	endpoint,
	ttlMs = 5 * 60 * 1000,
	area = "session",
	validate,
	skipCache = false,
}: ApiCacheOptions<T>): Promise<T> {
	// Se skipCache = true, pular verificação de cache e sempre buscar da API
	if (!skipCache) {
		const cached = storage.get<T>(key, {
			area,
			validate: validate as any,
		});
		if (cached) return cached;
	}

	const data = await api(endpoint);

	// Ainda salvar no cache (para outras partes), mas sempre buscar fresco quando skipCache = true
	if (!skipCache) {
		storage.set<T>(key, data, { area, ttlMs });
	}

	return data as T;
}

/**
 * Busca dados da API com cache inteligente baseado em ETag.
 *
 * Fluxo:
 * 1. Verifica se há dados em cache com ETag
 * 2. Faz requisição com If-None-Match (ETag do cache)
 * 3. Se servidor retorna 304: usa dados do cache local
 * 4. Se servidor retorna 200: atualiza cache com novos dados + ETag
 *
 * @example
 * const { data, fromCache, etag } = await getWithCacheETag<Tool>({
 *   key: 'tool:converter-para-bitmap',
 *   endpoint: '/tools/converter-para-bitmap',
 *   ttlMs: 30 * 60 * 1000, // 30 minutos
 *   area: 'local'
 * });
 */
export async function getWithCacheETag<T = any>({
	key,
	endpoint,
	ttlMs = 5 * 60 * 1000,
	area = "session",
	validate,
	forceRefresh = false,
}: ApiCacheETagOptions<T>): Promise<ApiCacheETagResult<T>> {
	// 1. Verificar cache local (com metadados incluindo ETag)
	const { data: cachedData, meta } = storage.getWithMeta<T>(key, {
		area,
		validate: validate as any,
	});

	const cachedEtag = meta?.etag;

	// 2. Se forceRefresh, não enviar ETag (força busca completa)
	// Se tem cache válido com ETag, enviar If-None-Match
	const ifNoneMatch = forceRefresh ? undefined : cachedEtag;

	try {
		// 3. Fazer requisição com ETag (se disponível)
		const result = await apiWithETag(endpoint, {}, { ifNoneMatch });

		// 4. Se 304 Not Modified e temos cache válido, usar cache
		if (result.notModified && cachedData !== null) {
			console.log(`🟢 [CACHE] HIT (304) - ${key}`);
			return {
				data: cachedData,
				fromCache: true,
				etag: cachedEtag,
			};
		}

		// 5. Dados novos - atualizar cache com novo ETag
		const newData = result.data as T;
		const newEtag = result.etag?.replace(/"/g, "") || undefined;

		// Salvar no cache com ETag
		storage.set<T>(key, newData, {
			area,
			ttlMs,
			etag: newEtag,
		});

		console.log(`🔵 [CACHE] MISS - ${key}`, { etag: newEtag });

		return {
			data: newData,
			fromCache: false,
			etag: newEtag,
		};
	} catch (error) {
		// Se falhou mas temos cache válido, usar como fallback
		if (cachedData !== null && !forceRefresh) {
			console.warn(`⚠️ [CACHE] FALLBACK - ${key} (erro na API, usando cache)`);
			return {
				data: cachedData,
				fromCache: true,
				etag: cachedEtag,
			};
		}

		// Sem cache e falhou - propagar erro
		throw error;
	}
}

/**
 * Limpa cache de uma chave específica
 */
export function clearCache(key: string, area: StorageArea = "session") {
	storage.remove(key, area);
}

/**
 * Limpa todos os caches de ferramentas (tools)
 * Busca por chaves que começam com 'tool' ou 'cdr:tools'
 */
export function clearAllToolsCache(area: StorageArea = "session") {
	const store = area === "local" ? window.localStorage : window.sessionStorage;
	const keysToRemove: string[] = [];

	for (let i = 0; i < store.length; i++) {
		const key = store.key(i);
		if (key && (key.startsWith("tool") || key.startsWith("cdr:tools"))) {
			keysToRemove.push(key);
		}
	}

	keysToRemove.forEach((key) => store.removeItem(key));

	console.log(
		`🗑️ [CACHE] Limpou ${keysToRemove.length} chaves de tools`,
		keysToRemove,
	);

	return keysToRemove.length;
}
